import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { ThemeMode } from '../types/config';
import useMediaQuery from '@mui/material/useMediaQuery';
import { atomWithStorage } from 'jotai/utils';
import { useAtom } from 'jotai';

const LOCAL_STORAGE_THEME_MODE_KEY = 'ood-theme-mode';

export enum OodThemeMode {
	LIGHT = 'light',
	DARK = 'dark',
	SYSTEM = 'system',
}

const initialValue: UseThemeModeResult = {
	oodMode: OodThemeMode.SYSTEM,
	setOodMode: () => {},
	mode: ThemeMode.LIGHT,
};

const oodModeAtom = atomWithStorage<OodThemeMode>(LOCAL_STORAGE_THEME_MODE_KEY, OodThemeMode.SYSTEM);

type UseThemeModeResult = Readonly<{
	oodMode: OodThemeMode;
	setOodMode: (args: OodThemeMode) => void;
	mode: ThemeMode;
}>;

const ThemeModeContext = createContext(initialValue);
const ThemeModeProvider: FC<PropsWithChildren> = ({ children }) => {
	const [oodMode, setOodMode] = useAtom(oodModeAtom);
	const isSystemDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

	const [mode, setMode] = useState(ThemeMode.LIGHT);
	const [isFirstRender, setIsFirstRender] = useState(true);

	useEffect(() => {
		if (isFirstRender) {
			// whenever system mode updates, update application mode too
			setIsFirstRender(false);
		}
		switch (oodMode) {
			case OodThemeMode.LIGHT:
				setMode(ThemeMode.LIGHT);
				break;
			case OodThemeMode.DARK:
				setMode(ThemeMode.DARK);
				break;
			default:
				setMode(isSystemDarkMode ? ThemeMode.DARK : ThemeMode.LIGHT);
				break;
		}
	}, [isSystemDarkMode, oodMode, isFirstRender, setIsFirstRender, mode, setMode]);

	return <ThemeModeContext.Provider value={{ oodMode, setOodMode, mode }}>{children}</ThemeModeContext.Provider>;
};

const useThemeMode = (): UseThemeModeResult => {
	return useContext(ThemeModeContext);
};

export { ThemeModeProvider, useThemeMode };
